"use client";

import { PORTALS } from ".constants/portals";
import {
  PortalModal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  Button,
  Icon,
  usePortalContext,
} from "@cloai/uikit";
import { motion } from "framer-motion";
import { map } from "lodash";
import { useLocale } from "next-intl";
import Image from "next/image";
import { memo, useCallback } from "react";

import {
  PhotoSettings,
  useUndressContext,
  useUndressSettingsContext,
} from "@/providers";
import { cn } from "@/utils/cn";
import { getLocalizedName } from "@/utils/getLocalizedName";

import { STYLES, MOTION_VARIANTS } from "./vars";

export const PosesPortal = memo(() => {
  const {
    initialSettings,
    clickFileId,
    clickSettings,
    clickUndressingFile,
    onReUseFaceObj,
    reUseFaceObj,
    updatePhotoSetting,
  } = useUndressContext();
  const { poses } = useUndressSettingsContext();
  const { closePortal, openPortal } = usePortalContext();

  const locale = useLocale();

  const { costume, pose } = clickSettings;

  const handleClick = useCallback(
    (itemType: keyof PhotoSettings, itemId: string) => () => {
      updatePhotoSetting(clickFileId, itemType, itemId);
      closePortal();

      if (costume === "default") {
        return openPortal(PORTALS.USER_FLOW_COSTUMES);
      }

      if (reUseFaceObj) {
        return onReUseFaceObj();
      }

      if (!clickUndressingFile) {
        return openPortal(PORTALS.USER_FLOW_UPLOAD);
      }
    },
    [
      clickFileId,
      clickUndressingFile,
      closePortal,
      costume,
      onReUseFaceObj,
      openPortal,
      reUseFaceObj,
      updatePhotoSetting,
    ],
  );

  const onSkipPose = useCallback(() => {
    closePortal();
    reUseFaceObj
      ? onReUseFaceObj()
      : !clickUndressingFile && openPortal(PORTALS.USER_FLOW_UPLOAD);
  }, [
    clickUndressingFile,
    closePortal,
    onReUseFaceObj,
    openPortal,
    reUseFaceObj,
  ]);

  return (
    <PortalModal
      portalId={PORTALS.USER_FLOW_POSES}
      style={STYLES.portal.variants.wide}
    >
      <ModalHeader>
        <div>CHOOSE POSE</div>
        <div>(optional)</div>
      </ModalHeader>

      <ModalContent className="@container">
        <motion.div
          variants={MOTION_VARIANTS.container}
          initial="hidden"
          animate="visible"
          className={cn(
            "grid grid-cols-2 gap-2",
            "@lg:grid-cols-3",
            "@2xl:grid-cols-4 @2xl:gap-3",
          )}
        >
          {map(poses, (item, Index) => (
            <motion.div
              key={item.id}
              variants={MOTION_VARIANTS.item}
              custom={Index}
            >
              <div
                className={cn(
                  STYLES.wrapper.base,
                  STYLES.wrapper.variants.square,
                )}
                onClick={handleClick("pose", item.id)}
              >
                <Image
                  src={item.image ?? ""}
                  fill
                  sizes="(min-width: 768px) 25vw, 50vw"
                  alt=""
                  priority
                  className={STYLES.image}
                />
                <span
                  className={cn(STYLES.text.base, STYLES.text.variants.default)}
                >
                  {getLocalizedName(item, locale)}
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </ModalContent>

      {costume !== "default" && pose === initialSettings.pose && (
        <ModalFooter>
          <Button
            startContent={<Icon icon="double_arrow" />}
            color="secondary"
            wide
            centerContent
            className="ml-auto"
            onClick={onSkipPose}
          >
            Skip Pose
          </Button>
        </ModalFooter>
      )}
    </PortalModal>
  );
});

PosesPortal.displayName = "PosesPortal";
